// src/sim/ai/coordination/shout.ts — shout audibility with wall attenuation (D-15).
//
// isShoutAudible mirrors the Bresenham wall-walk pattern from light-level.ts,
// but adapted for tile-space (not pixel-space):
//   - tile-graph distance via tileGraphDistance (A* path cost, walls must be walked around)
//   - + wallCount * P4_SHOUT_WALL_PENALTY_TILES for each wall tile the straight line crosses
//   - audible iff effective <= listenerHearingRangeTiles
//
// Euclidean radius rejected (Pitfall 15 shape). See D-15 and Pattern 8 in 04-RESEARCH.md.
//
// Source: .planning/phases/04-ai/04-CONTEXT.md D-15; 04-RESEARCH.md Pattern 8 L836-860.

import { tileGraphDistance } from '../pathing';
// P7 POL-06 / D-12: shout wall penalty read through the tuning cache (./constants is the fallback default).
import { getTuning } from '../../content/cache';
import type { WalkabilityGrid } from '../../../shared/types/map';

/**
 * Determines whether a shout from sourceTile reaches listenerTile.
 *
 * Algorithm:
 *   1. Compute A* path distance (tile graph cost). If Infinity → not audible.
 *   2. Walk the straight tile-line from source→listener, counting wall tile intersections.
 *   3. effectiveDistance = dist + wallCount * P4_SHOUT_WALL_PENALTY_TILES
 *   4. audible iff effectiveDistance <= listenerHearingRangeTiles
 *
 * @param sourceTile     - [tileX, tileY] of the shouting entity.
 * @param listenerTile   - [tileX, tileY] of the listening entity.
 * @param listenerHearingRangeTiles - hearing range of the listener in tiles.
 * @param grid           - walkability grid (0=walkable, 1=blocked).
 */
export function isShoutAudible(
  sourceTile: readonly [number, number],
  listenerTile: readonly [number, number],
  listenerHearingRangeTiles: number,
  grid: WalkabilityGrid,
): { audible: boolean; effectiveDistance: number } {
  const dist = tileGraphDistance(
    sourceTile[0],
    sourceTile[1],
    listenerTile[0],
    listenerTile[1],
    grid,
  );

  if (!isFinite(dist)) {
    return { audible: false, effectiveDistance: Infinity };
  }

  // Count wall tiles intersected by the straight line from source to listener.
  // Mirrors the Bresenham-ish pattern from light-level.ts isBlockedByWall,
  // but counts all walls rather than short-circuiting on the first one.
  let wallCount = 0;
  const dx = listenerTile[0] - sourceTile[0];
  const dy = listenerTile[1] - sourceTile[1];
  const steps = Math.ceil(Math.hypot(dx, dy));

  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const tx = Math.floor(sourceTile[0] + dx * t);
    const ty = Math.floor(sourceTile[1] + dy * t);
    const row = grid[ty];
    if (row !== undefined && row[tx] === 1) {
      wallCount++;
    }
  }

  const effective = dist + wallCount * getTuning().shoutWallPenaltyTiles;
  return { audible: effective <= listenerHearingRangeTiles, effectiveDistance: effective };
}
